import React from 'react';
import { Text, View, StyleSheet } from '@react-pdf/renderer';
import { calculateLost } from 'utils/calculateLost';
import { formatCurrency, formatNumber } from 'utils/formatNumbers';
import { InventoryReportTypeEnum } from 'types/inventory';
import { calculateInvoicingValues } from 'components/modules/EditHarvestSummaryModal/utils';

// Create styles
const styles = StyleSheet.create({
  row: {
    width: '100%',
    flexDirection: 'row',
  },
  cell: {
    backgroundColor: '#F9F4DA',
    color: '#53575A',
    fontFamily: 'Trebuchet',
    width: 50,
    height: 30,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: 10,
    borderRight: '1px solid #BABABA',
  },
  cellSmallFont: {
    fontSize: 9, // 1px smaller for large currency values
  },
});

// Helper function to check if currency value needs smaller font
const needsSmallFont = (value: number): boolean => {
  return Math.abs(value) >= 1000000; // 7+ digits
};

// Create Document Component
const InvoiceRow = ({
  isTotal = false,
  isOdd = false,
  row,
  site = {},
  reportType,
}: {
  isTotal?: boolean | undefined;
  isOdd?: boolean | undefined;
  row: any;
  site?: any;
  reportType?: any;
}) => {
  if (!row) return null;
  const sitePricingDetails =
    site?.pricingDetails?.filter((pd: any) => pd.itemSku === row.sku._id) || [];
  const siteReplacementPrice =
    sitePricingDetails.length > 0
      ? sitePricingDetails[0].replacementPrice
      : row?.sku?.pricePerReplacement;
  const lost = calculateLost(row);

  const siteDeliveryPrice =
    sitePricingDetails.length > 0
      ? sitePricingDetails[0].deliveryPrice
      : row?.sku?.pricePerDelivery;
  return (
    <>
      {!isTotal && (
        <View style={styles.row}>
          <View
            style={{
              ...styles.cell,
              width: 78,
              backgroundColor: `${
                isTotal ? '#ECE5C3' : isOdd ? '#F9F4DA' : '#FFFCE8'
              }`,
            }}>
            <Text>{row.sku.name}</Text>
          </View>{' '}
          <View
            style={{
              ...styles.cell,
              width: 78,
              backgroundColor: `${
                isTotal ? '#ECE5C3' : isOdd ? '#F9F4DA' : '#FFFCE8'
              }`,
            }}>
            <Text> {formatNumber(lost)}</Text>
          </View>
          <View
            style={{
              ...styles.cell,
              width: 78,
              backgroundColor: `${
                isTotal ? '#ECE5C3' : isOdd ? '#F9F4DA' : '#FFFCE8'
              }`,
            }}>
            <Text
              style={
                needsSmallFont(Number(siteReplacementPrice))
                  ? styles.cellSmallFont
                  : {}
              }>
              {formatCurrency(Number(siteReplacementPrice)) || '-'}
            </Text>
          </View>
          <View
            style={{
              ...styles.cell,
              width: 78,
              backgroundColor: `${
                isTotal ? '#E7A9B0' : isOdd ? '#E9C3C7' : '#F6D7DA'
              }`,
            }}>
            <Text
              style={
                needsSmallFont(lost * (Number(siteReplacementPrice) || 0))
                  ? styles.cellSmallFont
                  : {}
              }>
              {formatCurrency(lost * (Number(siteReplacementPrice) || 0))}
            </Text>
          </View>
          {reportType === InventoryReportTypeEnum.NO_INVENTORY && (
            <>
              <View
                style={{
                  ...styles.cell,
                  width: 78,
                  backgroundColor: `${
                    isTotal ? '#ECE5C3' : isOdd ? '#F9F4DA' : '#FFFCE8'
                  }`,
                }}>
                <Text> {formatNumber(row.ret + lost)}</Text>
              </View>
              <View
                style={{
                  ...styles.cell,
                  width: 78,
                  backgroundColor: `${
                    isTotal ? '#ECE5C3' : isOdd ? '#F9F4DA' : '#FFFCE8'
                  }`,
                }}>
                <Text
                  style={
                    needsSmallFont(Number(siteDeliveryPrice))
                      ? styles.cellSmallFont
                      : {}
                  }>
                  {formatCurrency(Number(siteDeliveryPrice)) || '-'}
                </Text>
              </View>
              <View
                style={{
                  ...styles.cell,
                  width: 78,
                  backgroundColor: `${
                    isTotal ? '#9CC5E7' : isOdd ? '#AECFEA' : '#BFDCF3'
                  }`,
                  borderRight: 0,
                }}>
                <Text
                  style={
                    needsSmallFont((row.ret + lost) * (siteDeliveryPrice || 0))
                      ? styles.cellSmallFont
                      : {}
                  }>
                  {formatCurrency((row.ret + lost) * (siteDeliveryPrice || 0))}
                </Text>
              </View>
            </>
          )}
        </View>
      )}
      {/* totals */}
      {isTotal && (
        <View style={[styles.row, { fontWeight: 'bold' }]}>
          <View
            style={{
              ...styles.cell,
              width: 78,
              backgroundColor: `${
                isTotal ? '#ECE5C3' : isOdd ? '#F9F4DA' : '#FFFCE8'
              }`,
            }}>
            <Text>Total Items</Text>
          </View>{' '}
          <View
            style={{
              ...styles.cell,
              width: 78,
              backgroundColor: `${
                isTotal ? '#ECE5C3' : isOdd ? '#F9F4DA' : '#FFFCE8'
              }`,
            }}>
            <Text> {formatNumber(row.lost)}</Text>
          </View>
          <View
            style={{
              ...styles.cell,
              width: 78,
              backgroundColor: `${
                isTotal ? '#ECE5C3' : isOdd ? '#F9F4DA' : '#FFFCE8'
              }`,
            }}>
            <Text>-</Text>
          </View>
          <View
            style={{
              ...styles.cell,
              width: 78,
              backgroundColor: `${
                isTotal ? '#E7A9B0' : isOdd ? '#E9C3C7' : '#F6D7DA'
              }`,
            }}>
            <Text
              style={
                needsSmallFont(row.totalLostFees) ? styles.cellSmallFont : {}
              }>
              {formatCurrency(row.totalLostFees)}
            </Text>
          </View>
          {reportType === InventoryReportTypeEnum.NO_INVENTORY && (
            <>
              <View
                style={{
                  ...styles.cell,
                  width: 78,
                  backgroundColor: `${
                    isTotal ? '#ECE5C3' : isOdd ? '#F9F4DA' : '#FFFCE8'
                  }`,
                }}>
                <Text> {formatNumber(row.used)}</Text>
              </View>
              <View
                style={{
                  ...styles.cell,
                  width: 78,
                  backgroundColor: `${
                    isTotal ? '#ECE5C3' : isOdd ? '#F9F4DA' : '#FFFCE8'
                  }`,
                }}>
                <Text>-</Text>
              </View>
              <View
                style={{
                  ...styles.cell,
                  width: 78,
                  backgroundColor: `${
                    isTotal ? '#9CC5E7' : isOdd ? '#AECFEA' : '#BFDCF3'
                  }`,
                  borderRight: 0,
                }}>
                <Text
                  style={
                    needsSmallFont(row.totalUsedFees)
                      ? styles.cellSmallFont
                      : {}
                  }>
                  {formatCurrency(row.totalUsedFees)}
                </Text>
              </View>
            </>
          )}
        </View>
      )}
    </>
  );
};

export default InvoiceRow;
